import React from "react";
import { useRouter } from "next/router";
import { Button } from "@src/lib/ui/Button";
import SearchIcon from "@src/lib/icons/18px/search";
import { cn } from "@src/lib/utils/cn";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import Text from "@src/lib/ui/Text";
import { Command, CommandInput, CommandList } from "@src/lib/ui/Command";
import { useDebouncedInputValue } from "@src/lib/hooks/useDebouncedInputValue";
import useAllJumpToItems, {
  JumpToItem,
} from "@src/features/app/ui/jumpToBar/hooks/useAllJumpToItems";
import useAllRecentlyViewedItems from "@src/features/app/ui/jumpToBar/hooks/useAllRecentlyViewedItems";
import {
  getLinkToItem,
  isActionItemType,
  itemDescription,
} from "@src/features/app/top-nav/utils";
import JumpToBarItems from "@src/features/app/top-nav/JumpToBarItems";
import { useAdminToolsMenu } from "@src/features/app/top-nav/AdminTools";

const TOP_NAV_SEARCH_WIDTH = "w-72";

const JumpToBar: React.FC<{ hidden?: boolean }> = ({ hidden: hideTrigger }) => {
  const router = useRouter();
  const [open, setOpen] = React.useState(false);
  const {
    value: search,
    setValue: setSearch,
    debouncedValue: debouncedSearch,
  } = useDebouncedInputValue({ initialValue: "", delay: 300 });
  const { items: allJumpToItems, loading } = useAllJumpToItems({
    search: debouncedSearch,
  });
  const { items: recentlyViewedItems } = useAllRecentlyViewedItems();
  const { dialog: adminToolsDialog, openAdminTools } = useAdminToolsMenu();
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);
  const selectItem = React.useCallback(
    (item: JumpToItem) => {
      if (isActionItemType(item) && item.action === "admin-tools") {
        setOpen(false);
        setSearch("");
        openAdminTools();
        return;
      }
      const link = getLinkToItem(item);
      setOpen(false);
      setSearch("");
      router.push(link);
    },
    [openAdminTools, router, setSearch],
  );
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        {open && hideTrigger && (
          <div
            className="fixed inset-0 z-40 bg-black/30 data-[state=open]:animate-in data-[state=open]:fade-in-0"
            data-state="open"
            onClick={() => setOpen(false)}
          />
        )}
        {hideTrigger ? (
          <PopoverTrigger asChild>
            <span
              aria-hidden
              className="pointer-events-none fixed top-[30vh] left-1/2 size-0"
            />
          </PopoverTrigger>
        ) : (
          <div className={cn("flex items-center gap-2", TOP_NAV_SEARCH_WIDTH)}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                // Nested radius (design.md › Radius): the trigger sits 8px
                // inside the rounded-xl (12px) content card at md+, so
                // 12 − 8 = 4px → rounded. Below md TopNav is px-1, so the
                // tighter 4px inset gives 12 − 4 = 8px → rounded-md-2.
                className="relative h-8 min-h-8 w-full rounded-md-2 pl-8 md:rounded"
                data-testid="jump-to-bar-trigger"
                asChild
              >
                <div className="flex items-center justify-start">
                  <SearchIcon className="absolute top-1/2 left-2 -translate-y-1/2 text-icon-tertiary" />
                  <Text
                    variant="compact-description"
                    className="flex-1 text-text-tertiary"
                  >
                    Quick find...
                  </Text>
                  <div className="absolute top-1/2 right-2 -translate-y-1/2 text-2xs-v4 text-text-tertiary">
                    ⌘K
                  </div>
                </div>
              </Button>
            </PopoverTrigger>
          </div>
        )}
        <PopoverContent
          className="w-[480px] p-0 shadow-2xl"
          align="start"
          side="bottom"
          sideOffset={0}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search"
              value={search}
              onValueChange={setSearch}
              data-testid="jump-to-bar-input"
            />
            <CommandList>
              <JumpToBarItems
                query={search}
                recentlyViewedItems={recentlyViewedItems}
                allItems={allJumpToItems}
                loading={loading}
                onSelectItem={selectItem}
                itemDescription={itemDescription}
                closePopover={() => setOpen(false)}
              />
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {adminToolsDialog}
    </>
  );
};

export default JumpToBar;
